import React, { useState } from "react";
import Select from "react-select";
import {CustomMultiSelectStyles} from "../../constants/index.js";
import {useDarkMode} from "../../utils/darkModeContext.js";
import {useLanguage} from "../../utils/languageContext.js";
import {getTranslation} from "../../utils/translate.js";

function CustomCheckboxOption(props) {
    const { innerProps, isFocused, isSelected, label } = props;
    const { isDarkMode } = useDarkMode();

    return (
        <div
            {...innerProps}
            style={{
                display: "flex",
                justifyContent: "space-between", // Space out label and checkbox
                alignItems: "center",
                backgroundColor: isSelected 
                    ? (isDarkMode ? '#1c2128' : '#f3f4f6')
                    : isFocused 
                        ? (isDarkMode ? '#1c2128' : '#f3f4f6')
                        : (isDarkMode ? '#0d1117' : '#fff'),
                color: isDarkMode ? '#c9d1d9' : '#374151',
                cursor: "pointer",
                height: "36px",
                width: "100%",
                padding: "0 8px",
            }}

        >
            {label}
            <input
                type="checkbox"
                checked={isSelected}
                disabled={isSelected}
                onChange={() => {}}
            />
        </div>
    );
}

const CustomMultiSelect = ({
    label,
    options,
    placeholder = "Select...",
    value,
    onChange,
    margin = "mb-4",
    isRequired = false,
    noOptionsMessage,
    error,
    setError,
    fieldName,
    isSingle = false,
    /** Render the option menu in a portal (document.body) with fixed positioning — fixes clipping/stacking inside modals and overflow containers. */
    portalMenu = false,
    ...restProps
}) => {
    const { isDarkMode } = useDarkMode();
    const { language } = useLanguage();
    /** When `value` is passed from parent, react-select stays fully controlled (fixes modal reset / parent-driven clears). */
    const isControlled = value !== undefined;
    const [internalValue, setInternalValue] = useState([]);
    const selectedForSelect = isControlled
        ? Array.isArray(value)
            ? value
            : []
        : internalValue;

    const handleChange = (selected) => {
        if (isSingle && selected && selected.length > 0) {
            const singleSelection = [selected[selected.length - 1]];
            if (!isControlled) setInternalValue(singleSelection);
            onChange && onChange(singleSelection);
            
            // Clear error if a value is selected
            if (singleSelection.length > 0 && error && setError && fieldName) {
                setError(prev => {
                    const newErrors = {...prev};
                    delete newErrors[fieldName];
                    return newErrors;
                });
            }
            return;
        }

        const next = selected || [];
        if (!isControlled) setInternalValue(next);
        onChange && onChange(next);
        
        // Clear error if at least one value is selected
        if (next && next.length > 0 && error && setError && fieldName) {
            setError(prev => {
                const newErrors = {...prev};
                delete newErrors[fieldName];
                return newErrors;
            });
        }
    };

    const dynamicPlaceholder =
        selectedForSelect && selectedForSelect.length > 0
            ? selectedForSelect.map((option) => option.label).join(", ")
            : placeholder;

    const menuZ = portalMenu ? 10050 : undefined;

    const customStyles = {
        ...CustomMultiSelectStyles,
        ...(portalMenu && {
            menuPortal: (provided) => ({
                ...provided,
                zIndex: menuZ
            })
        }),
        control: (provided, state) => ({
            ...CustomMultiSelectStyles.control(provided, state),
            backgroundColor: isDarkMode ? '#0d1117' : '#ffffff',
            borderColor: error 
                ? (isDarkMode ? '#ef4444' : '#ef4444')
                : (isDarkMode ? (state.isFocused ? '#30363d' : '#30363d') : (state.isFocused ? '#cfcfcf' : '#e5e7eb')),
            boxShadow: error
                ? (isDarkMode ? '0 0 0 2px rgba(239, 68, 68, 0.2)' : '0 0 0 2px rgba(239, 68, 68, 0.2)')
                : (state.isFocused ? (isDarkMode ? '0 0 0 2px rgba(48, 54, 61, 0.1)' : '0 0 0 2px rgba(0, 0, 0, 0.1)') : 'none'),
            '&:hover': {
                borderColor: error 
                    ? (isDarkMode ? '#ef4444' : '#ef4444')
                    : (isDarkMode ? '#30363d' : '#cfcfcf')
            }
        }),
        placeholder: (provided) => ({
            ...provided,
            color: isDarkMode ? "#6e7681" : "#9CA3AF",
        }),
        multiValue: (provided) => ({
            ...provided,
            backgroundColor: isDarkMode ? '#161b22' : '#f3f4f6',
        }),
        multiValueLabel: (provided) => ({
            ...provided,
            color: isDarkMode ? '#c9d1d9' : '#374151',
        }),
        multiValueRemove: (provided) => ({
            ...provided,
            color: isDarkMode ? '#c9d1d9' : '#374151',
            '&:hover': {
                backgroundColor: isDarkMode ? '#30363d' : '#e5e7eb',
                color: isDarkMode ? '#c9d1d9' : '#374151',
            }
        }),
        menu: (provided, state) => ({
            ...CustomMultiSelectStyles.menu(provided, state),
            backgroundColor: isDarkMode ? '#0d1117' : '#ffffff',
            borderColor: isDarkMode ? '#30363d' : '#e5e7eb',
            ...(portalMenu ? { zIndex: menuZ } : {})
        }),
        menuList: (provided) => ({
            ...CustomMultiSelectStyles.menuList(provided),
            backgroundColor: isDarkMode ? '#0d1117' : '#ffffff',
            ...(portalMenu
                ? {
                      maxHeight: 'min(320px, 50vh)',
                      overflowY: 'auto',
                      WebkitOverflowScrolling: 'touch'
                  }
                : {})
        }),
    };

    return (
        <div className={`relative w-full ${margin}`}>
            {label && (
                <label className="text-sm font-bold block text-fontColor dark:text-[#c9d1d9] mb-2">
                    {label}
                    {isRequired && <span className="text-red-500 dark:text-red-400 ml-1">*</span>}
                </label>
            )}
            <div className={error ? "ring-2 ring-red-500 dark:ring-red-400 rounded-md" : ""}>
                <Select
                options={options}
                isMulti
                value={selectedForSelect}
                components={{ Option: CustomCheckboxOption }}
                onChange={handleChange}
                placeholder={dynamicPlaceholder}
                closeMenuOnSelect={isSingle}
                hideSelectedOptions={false}
                menuPortalTarget={portalMenu ? (typeof document !== "undefined" ? document.body : null) : undefined}
                menuPosition={portalMenu ? "fixed" : undefined}
                menuPlacement="auto"
                className="react-select-container"
                classNamePrefix="react-select"
                noOptionsMessage={noOptionsMessage || (() => getTranslation("common.noOptions", language) || "No options")}
                styles={customStyles}
                theme={(theme) => ({
                    ...theme,
                    colors: {
                        ...theme.colors,
                        primary: '#3b82f6',
                        primary75: '#60a5fa',
                        primary50: '#93c5fd',
                        primary25: '#dbeafe',
                        neutral0: isDarkMode ? '#0d1117' : '#ffffff',
                        neutral5: isDarkMode ? '#161b22' : '#f9fafb',
                        neutral10: isDarkMode ? '#1c2128' : '#f3f4f6',
                        neutral20: isDarkMode ? '#30363d' : '#e5e7eb',
                        neutral30: isDarkMode ? '#484f58' : '#d1d5db',
                        neutral40: isDarkMode ? '#6e7681' : '#9ca3af',
                        neutral50: isDarkMode ? '#8b949e' : '#6b7280',
                        neutral60: isDarkMode ? '#b1bac4' : '#4b5563',
                        neutral70: isDarkMode ? '#c9d1d9' : '#374151',
                        neutral80: isDarkMode ? '#f0f6fc' : '#1f2937',
                        neutral90: isDarkMode ? '#ffffff' : '#111827',
                    },
                })}
                {...restProps}
            />
            </div>
            {error && <p className="text-red-500 dark:text-red-400 text-xs mt-1">{error}</p>}
        </div>
    );
};

export default CustomMultiSelect;
